import { Link, useParams } from "react-router-dom";
import { useState } from "react";
import { ArrowLeft, Check, X, FileText, Loader2, Workflow, ArrowRight } from "lucide-react";
import { productBySlug, CONTACT_EMAIL } from "../data/products";
import { api, formatApiError } from "../lib/api";
import WaitlistForm from "../components/WaitlistForm";
import WaitlistModal from "../components/WaitlistModal";
import DecisionTreeDownload from "../components/DecisionTreeDownload";
import CarpetCardDownload from "../components/CarpetCardDownload";
import TypeBadge from "../components/TypeBadge";
import Seo from "../components/Seo";

const PAYMENTS_ENABLED = process.env.REACT_APP_PAYMENTS_ENABLED === "true";

const Section = ({ label, children }) => (
  <div className="border-t border-border pt-8">
    <p className="mono-label">{label}</p>
    <div className="mt-4">{children}</div>
  </div>
);

function BuyBox({ product }) {
  const [email, setEmail] = useState("");
  const [subscribe, setSubscribe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Payments are gated behind a feature flag until the official Stripe account is ready.
  // When disabled we show a launch-list CTA (existing waitlist infra), never a live checkout.
  if (!PAYMENTS_ENABLED) {
    return (
      <div className="mt-3" data-testid="launch-list-cta">
        <p className="mono-label text-muted-foreground">Planned launch price</p>
        <span className="font-display text-4xl font-medium text-foreground">{product.price}</span>
        <p className="mt-3 text-xs text-muted-foreground leading-relaxed">Expected one-time purchase at launch. Not yet available to buy; no payment is taken now.</p>
        <div className="mt-4 border border-primary/30 bg-primary/5 p-4">
          <p className="mono-label text-primary">Launching soon</p>
          <p className="mt-2 text-sm text-foreground/70 leading-relaxed">Be notified when the Low-Tox Home Blueprint becomes available for purchase.</p>
        </div>
        <WaitlistModal
          slug="low-tox-home-blueprint"
          name="The Low-Tox Home Blueprint"
          triggerLabel="Join the Blueprint Launch List"
          triggerTestid="blueprint-launch-list-btn"
          triggerClassName="group mt-4 w-full inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground font-medium text-sm px-6 py-3.5 hover:bg-primary-hover transition-colors shadow-[0_0_20px_rgba(0,229,153,0.3)] cursor-pointer"
          heading="Join the Blueprint Launch List"
          supportingText="Be notified when the Low-Tox Home Blueprint becomes available for purchase."
        />
      </div>
    );
  }

  const buy = async () => {
    if (!email) { setError("Please enter your email so we can deliver your download."); return; }
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/checkout/session", {
        product_slug: product.slug,
        origin_url: window.location.origin,
        email,
        subscribe,
      });
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error("No checkout URL received");
      }
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Unable to start checkout. Please try again.");
      setLoading(false);
    }
  };

  return (
    <div className="mt-3">
      <span className="font-display text-4xl font-medium text-foreground">{product.price}</span>
      <p className="mt-3 text-xs text-muted-foreground leading-relaxed">One-time purchase. Instant digital download. Lifetime access to updates.</p>
      <input
        data-testid="buy-email"
        type="email"
        required
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="Email for your receipt & download"
        className="mt-4 w-full bg-surface border border-border px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none transition-colors"
      />
      {error && <p data-testid="checkout-error" className="mt-3 text-xs text-destructive">{error}</p>}
      <label className="mt-3 flex items-start gap-2.5 cursor-pointer select-none">
        <input
          data-testid="buy-subscribe-checkbox"
          type="checkbox"
          checked={subscribe}
          onChange={(e) => setSubscribe(e.target.checked)}
          className="mt-0.5 h-4 w-4 accent-primary shrink-0"
        />
        <span className="text-xs text-muted-foreground leading-relaxed">Subscribe me to Greenpapers, engineering-based publications on reducing environmental exposure. Unsubscribe anytime.</span>
      </label>
      <button
        data-testid="buy-now-btn"
        onClick={buy}
        disabled={loading}
        className="group mt-3 w-full inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground font-medium text-sm px-6 py-3.5 hover:bg-primary-hover transition-colors disabled:opacity-60 shadow-[0_0_20px_rgba(0,229,153,0.3)]"
      >
        {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Redirecting to checkout</> : "Buy Now"}
      </button>
      <p className="mt-3 text-[11px] text-muted-foreground text-center">Secure checkout powered by Stripe</p>
    </div>
  );
}

export default function ProductDetailPage() {
  const { slug } = useParams();
  const p = productBySlug(slug);
  // Purchasable only when the product is available AND payments are live.
  const purchasable = PAYMENTS_ENABLED && p && p.status === "available";

  if (!p) return (
    <div data-testid="product-notfound" className="mx-auto max-w-3xl px-5 py-32 text-center">
      <h1 className="font-display text-3xl">Product not found</h1>
      <Link to="/products" className="mt-6 inline-flex items-center gap-2 text-primary font-mono text-xs uppercase tracking-widest"><ArrowLeft className="h-4 w-4" /> Back to Products</Link>
    </div>
  );

  return (
    <div data-testid="product-detail">
      <Seo
        title={p.name}
        description={p.positioning}
        path={`/products/${p.slug}`}
        image={p.image}
        type="product"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Product",
          name: p.name,
          description:
            p.slug === "low-tox-home-blueprint"
              ? (purchasable
                  ? "A paid digital educational guide and residential environmental design manual for reducing cumulative exposure through prioritized, room-by-room decisions."
                  : "A digital educational guide and residential environmental design manual for reducing cumulative exposure through prioritized, room-by-room decisions. Launching soon; join the launch list to be notified when it becomes available.")
              : p.positioning,
          image: p.image ? [p.image] : ["https://myecoengineer.com/brand/mee-og.png"],
          brand: { "@type": "Brand", name: "My Eco Engineer" },
          category: "Digital educational guide",
          // Offer is included ONLY when the product is actually purchasable (payments live).
          // While collecting launch-list interest, we emit NO Offer/availability/price so we
          // do not signal a current commercial offer to search or AI systems.
          ...(purchasable && p.price
            ? {
                offers: {
                  "@type": "Offer",
                  price: String(p.price).replace(/[^0-9.]/g, ""),
                  priceCurrency: "USD",
                  availability: "https://schema.org/InStock",
                  url: `https://myecoengineer.com/products/${p.slug}`,
                },
              }
            : {}),
        }}
      />
      <section className="relative grain border-b border-border overflow-hidden">
        {p.image && (
          <>
            <img src={p.image} alt={p.name} className="absolute inset-0 h-full w-full object-cover opacity-20" />
            <div className="absolute inset-0" style={{ background: "linear-gradient(to right, #ffffff, #ffffffe6 45%, #ffffff80), linear-gradient(to top, #ffffff, transparent 65%)" }} />
          </>
        )}
        <div className="relative mx-auto max-w-7xl px-5 sm:px-8 py-20">
          <Link to="/products" className="inline-flex items-center gap-2 text-foreground/60 hover:text-primary font-mono text-[11px] uppercase tracking-widest transition-colors">
            <ArrowLeft className="h-4 w-4" /> Products
          </Link>
          <div className="mt-8 flex items-center gap-3">
            <TypeBadge type={p.type} />
            <span className={`px-2.5 py-1 font-mono text-[10px] uppercase tracking-widest border ${purchasable ? "border-primary/50 text-primary bg-primary/5" : "border-border text-muted-foreground"}`}>
              {purchasable ? "Available" : "Launching soon"}
            </span>
          </div>
          <h1 className="mt-5 font-display text-4xl sm:text-5xl tracking-tight max-w-3xl leading-[1.05]">{p.name}</h1>
          <p className="mt-5 max-w-2xl text-base md:text-lg text-foreground/65 leading-relaxed">{p.positioning}</p>
        </div>
      </section>

      <div className="mx-auto max-w-7xl px-5 sm:px-8 py-16 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-8 space-y-8">
          <Section label="Overview">
            <p className="text-base text-foreground/75 leading-relaxed">{p.overview}</p>
          </Section>

          {p.includes && p.includes.length > 0 && (
            <Section label="What's Included">
              <ul className="space-y-2.5">
                {p.includes.map((d, i) => (
                  <li key={i} className="flex gap-3 text-sm text-foreground/75"><Check className="h-4 w-4 text-primary shrink-0 mt-0.5" />{d}</li>
                ))}
              </ul>
            </Section>
          )}

          {p.designed_for.length > 0 && (
            <Section label="Designed For">
              <ul className="space-y-2.5">
                {p.designed_for.map((d, i) => (
                  <li key={i} className="flex gap-3 text-sm text-foreground/75"><Check className="h-4 w-4 text-primary shrink-0 mt-0.5" />{d}</li>
                ))}
              </ul>
            </Section>
          )}

          {p.scope.length > 0 && (
            <Section label="System Scope">
              <ul className="grid sm:grid-cols-2 gap-2.5">
                {p.scope.map((s, i) => (
                  <li key={i} className="flex gap-3 text-sm text-foreground/75"><span className="font-mono text-primary text-xs mt-0.5">{String(i + 1).padStart(2, "0")}</span>{s}</li>
                ))}
              </ul>
            </Section>
          )}

          {p.architecture.length > 0 && (
            <Section label="System Architecture">
              <div className="space-y-4">
                {p.architecture.map((a, i) => (
                  <div key={i} className="border border-border bg-surface p-5">
                    <h3 className="font-display text-lg font-medium text-foreground">{a.t}</h3>
                    <p className="mt-2 text-sm text-foreground/60 leading-relaxed">{a.d}</p>
                  </div>
                ))}
              </div>
            </Section>
          )}

          {p.not_do.length > 0 && (
            <Section label={`What This ${p.name} Does Not Do`}>
              <ul className="space-y-2.5">
                {p.not_do.map((d, i) => (
                  <li key={i} className="flex gap-3 text-sm text-foreground/60"><X className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />{d}</li>
                ))}
              </ul>
            </Section>
          )}

          {p.note && (
            <div data-testid="product-note" className="border border-border bg-surface/50 p-5">
              <p className="text-xs text-muted-foreground leading-relaxed">{p.note}</p>
            </div>
          )}

          {p.slug === "low-tox-home-blueprint" && (
            <div data-testid="blueprint-companion" className="relative border border-primary/30 bg-primary/5 p-6 sm:p-7">
              <p className="mono-label text-primary">Free companion tool</p>
              <div className="mt-3 flex items-start gap-4">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center border border-primary/40 text-primary bg-base">
                  <Workflow className="h-5 w-5" strokeWidth={1.5} />
                </span>
                <div>
                  <h3 className="font-display text-lg font-medium leading-snug">Cumulative Exposure Decision Tree</h3>
                  <p className="mt-2 text-sm text-foreground/65 leading-relaxed">
                    A calm, engineering-based tool for deciding which exposures to act on across the home, body, workplace, vehicle, and daily life. Included free alongside the Blueprint.
                  </p>
                  <div className="mt-4 flex flex-wrap gap-3">
                    <DecisionTreeDownload
                      testid="companion-download"
                      label="Download the Decision Tree"
                      className="inline-flex items-center gap-2 border border-primary text-primary px-4 py-2.5 font-mono text-[11px] uppercase tracking-widest hover:bg-primary hover:text-primary-foreground transition-colors"
                    />
                    <Link
                      to="/resources/cumulative-exposure-decision-tree"
                      data-testid="companion-view"
                      className="inline-flex items-center gap-1.5 text-foreground/60 hover:text-primary font-mono text-[11px] uppercase tracking-widest transition-colors"
                    >
                      View interactive version <ArrowRight className="h-3.5 w-3.5" />
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          )}

          {p.slug === "low-tox-home-blueprint" && (
            <div data-testid="carpet-card-companion" className="relative mt-5 border border-border bg-surface p-6 sm:p-7">
              <p className="mono-label text-primary">Free printable worksheet</p>
              <div className="mt-3 flex items-start gap-4">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center border border-primary/40 text-primary bg-base">
                  <FileText className="h-5 w-5" strokeWidth={1.5} />
                </span>
                <div>
                  <h3 className="font-display text-lg font-medium leading-snug">Carpet: Keep vs. Replace card</h3>
                  <p className="mt-2 text-sm text-foreground/65 leading-relaxed">
                    A one-page, print-and-tick quick-decision card for evaluating synthetic and other carpet by condition, not by material type. Join the email list and it's yours free.
                  </p>
                  <div className="mt-4 flex flex-wrap items-center gap-4">
                    <CarpetCardDownload
                      testid="carpet-card-companion-download"
                      label="Get the Carpet Card"
                      className="inline-flex items-center gap-2 border border-primary text-primary px-4 py-2.5 font-mono text-[11px] uppercase tracking-widest hover:bg-primary hover:text-primary-foreground transition-colors"
                    />
                    <Link
                      to="/resources/carpet-keep-vs-replace"
                      data-testid="carpet-card-companion-view"
                      className="inline-flex items-center gap-1.5 text-foreground/60 hover:text-primary font-mono text-[11px] uppercase tracking-widest transition-colors"
                    >
                      Read the guide <ArrowRight className="h-3.5 w-3.5" />
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* purchase / waitlist sidebar */}
        <div className="lg:col-span-4">
          <div className="lg:sticky lg:top-24 border border-border bg-surface p-7">
            <p className="mono-label">Format</p>
            <ul className="mt-4 space-y-2.5">
              {p.format.map((f, i) => (
                <li key={i} className="flex gap-3 text-sm text-foreground/75"><FileText className="h-4 w-4 text-primary shrink-0 mt-0.5" />{f}</li>
              ))}
            </ul>
            <div className="mt-7 border-t border-border pt-6">
              <p className="mono-label">Investment</p>
              {p.price ? (
                p.status === "available" ? (
                  <BuyBox product={p} />
                ) : (
                  <div className="mt-3">
                    <span className="font-display text-4xl font-medium text-foreground">{p.price}</span>
                    <p className="mt-4 text-xs text-muted-foreground leading-relaxed">Secure checkout is being finalized. Join the notify list below and you'll be first to purchase at launch.</p>
                    <div className="mt-5"><WaitlistForm product={p} /></div>
                  </div>
                )
              ) : (
                <div className="mt-3">
                  <p className="text-sm text-foreground/60 mb-4">This system is in development. Join the waitlist to be notified at launch.</p>
                  <WaitlistForm product={p} />
                </div>
              )}
            </div>
            <p className="mt-6 text-xs text-muted-foreground">Questions? <a href={`mailto:${CONTACT_EMAIL}`} className="text-primary hover:underline">{CONTACT_EMAIL}</a></p>
          </div>
        </div>
      </div>
    </div>
  );
}
